import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import { EmployeeCell, truncatableCell } from "../Shared/tableUi";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import moment from "moment";
import {
  Add,
  AddCircleOutline,
  AccountBalanceOutlined,
  AccountBalanceWalletOutlined,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  HighlightOff,
  FileUploadOutlined,
  HistoryEduOutlined,
  HourglassEmptyOutlined,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection, scrollTop } from "services/constants";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { Loader } from "components/Loader";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApproveTable, bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import { loanAdvanceHeader } from "pages/PayRoll/PayRollHeaders";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
  APP_BAR_HEIGHT,
} from "services/constants/designTokens";
import { BulkSelectionBar } from "../Shared/BulkSelectionBar";
import { usePayInputConfig, isImportAllowed, isSourceAllowed } from "../Shared/usePayInputConfig";
import { SourceTabs } from "../Shared/SourceTabs";
import { formatDate, formatMoney } from "../Shared/formatters";
import {
  ColGroup,
  ProxyScrollbar,
  StickyHeadStrip,
  fixedTableSx,
  headScrollCellSx,
  hiddenHeadCellSx,
  pinnedCellSx,
  pinnedColumns,
  scrollBodySx,
  timelineScopeSx,
  useStickyHorizontalScroll,
} from "../Shared/stickyScroll";
import { emptyLoanForm, emptyMigratedForm, recordToLoanForm } from "./loansData";
import { LoanFormPanel, LoanViewPanel, canCancelRequest, canEditRequest, statusColor } from "./LoanPanels";
import { MigratedLoanPanel } from "./MigratedLoanPanel";
import { LoanImport } from "./LoanImport";
import {
  ENDED_STATUSES,
  approveAdvanceRequest,
  cancelAdvanceRequest,
  listAdvanceRequests,
  rejectAdvanceRequest,
  saveAdvanceRequest,
  saveMigratedLoan,
} from "./loansApi";
import { useQuickActionFromUrl } from "components/GlobalSearch/useQuickActionFromUrl";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Pay Inputs > Loans & Advances, on the shared Pay Inputs list
// pattern (Penalties / Overtime). Records are advance requests
// (advance/request/, loansApi.js): added here or imported, they start as
// Pending and go to the employee's approver. Pending ones can be edited;
// anything not yet repaid can be cancelled (the API has no delete).

const SUMMARY_CARDS = [
  { key: "total", title: "Total Records", icon: DescriptionOutlined, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "pending", title: "Pending Approval", icon: HourglassEmptyOutlined, accent: "#F79009", tint: "#FFF4E5" },
  { key: "loans", title: "Active Loans", icon: AccountBalanceOutlined, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "advances", title: "Active Advances", icon: AccountBalanceWalletOutlined, accent: "#7A5AF8", tint: "#F1EDFF" },
];

// Approved and still running (not Pending and not ended).
const isActive = (row) => row.status !== "Pending" && !ENDED_STATUSES.includes(row.status);


const TONES = {
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
  purple: { bg: "#F1EDFF", fg: "#5B4BD5" },
  amber: { bg: "#FFF4E5", fg: "#B54708" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
};
const TYPE_TONES = { Loan: "purple", Advance: "blue" };
const SOURCE_TONES = { Imported: "slate", Manual: "amber" };

const NUMERIC_KEYS = new Set(["disbursed_amount", "repaid_amount", "outstanding_amount"]);

// Fixed column widths (px): header and body are separate tables
// (Shared/stickyScroll.js) and must share widths to line up.
const COLUMN_WIDTHS = {
  issue_date: 120,
  employee: 190,
  department: 160,
  record_type: 110,
  source: 110,
  description: 200,
  reason: 220,
  disbursed_amount: 150,
  repaid_amount: 130,
  outstanding_amount: 160,
  progress_label: 210,
  status: 110,
};
const PINNED_KEYS = new Set(["employee", "department"]);
// Employee, Department, Date always lead the list in every saved view and
// can't be dragged to another place in the view editor.
const FIXED_COLUMNS = ["employee", "department", "issue_date"];

const sum = (rows, key) => Math.round(rows.reduce((total, row) => total + (Number(row[key]) || 0), 0) * 100) / 100;
const count = (rows) => rows.length.toLocaleString("en-IN");

const ToneChip = ({ label, tone }) => {
  if (!label) return "–";
  const colors = TONES[tone] || TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{
        ...designType.captionText,
        fontWeight: designWeights.medium,
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: designRadii.compactButton,
        backgroundColor: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Mui.Box>
  );
};

// Loans: "8 of 12 instalments" over a bar with the % repaid. Advances: a
// plain grey label ("Single payroll recovery" / "Recovered in payroll").
const ProgressCell = ({ row }) => {
  if (!row.show_progress_bar) return <ToneChip label={row.progress_label} tone="slate" />;
  return (
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Typography sx={{ ...designType.captionText, color: designColors.slate700 }} noWrap>
        {row.progress_label}
      </Mui.Typography>
      <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <Mui.LinearProgress
          variant="determinate"
          value={row.progress_pct}
          aria-label={`${row.progress_pct}% repaid`}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            backgroundColor: designColors.slate100,
            "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: row.progress_pct >= 100 ? "#12B76A" : "#2878FF" },
          }}
        />
        <Mui.Typography sx={{ ...designType.captionText, width: 34, textAlign: "right" }}>{row.progress_pct}%</Mui.Typography>
      </Mui.Stack>
    </Mui.Box>
  );
};

export const LoansAndAdvances = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <LoansAndAdvancesScreen key={String(domain) + ":" + String(topbarCompanyId)} {...props} />;
};

const LoansAndAdvancesScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  // Same access as the advance-salary screen this replaced.
  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Payroll Processing", "Advance");
  const validCompany = Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  // Payroll > Configuration > Pay Input > Loans & Advances, same as Leave:
  // Enable turns this screen off and Allowed Sources decide the Add menu.
  // If the settings can't be loaded, everything stays on.
  const { config: payInputConfig, loading: configLoading } = usePayInputConfig(domain, topbarCompanyId, "LOAN_ADVANCE", validCompany);
  const configEnabled = payInputConfig?.enabled !== false;
  // Approval in Pay Input Configuration (same as Shift Earnings / Leave):
  // without it the Status column, Pending Approval card and Approve / Reject
  // are not shown.
  const approvalRequired = !payInputConfig || payInputConfig.approvalRequired === "yes";
  const manualAllowed = isSourceAllowed(payInputConfig, "MANUAL");
  const importAllowed = isImportAllowed(payInputConfig);

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mutationRef = useRef(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [loanPanel, setLoanPanel] = useState({ open: false, mode: "add", rowId: null, initial: null });
  const [migratedPanel, setMigratedPanel] = useState({ open: false, initial: null });
  const [viewRowId, setViewRowId] = useState(null);
  // Rows to cancel + the reason typed in the confirm dialog.
  const [cancelIds, setCancelIds] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  // Bulk Approve / Reject: { status: "Approved" | "Rejected", ids } + the
  // comment (Approve, optional) or reason (Reject, required).
  const [statusAction, setStatusAction] = useState(null);
  const [statusNote, setStatusNote] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setSelectedIds(new Set());
    if (!validCompany || !domain || !userDetails?.id) {
      setAllRows([]);
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    listAdvanceRequests(domain, topbarCompanyId, userDetails.id, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setAllRows(rows); })
      // Errors show as a toast (Penalties pattern); the table stays empty.
      .catch((error) => { if (!controller.signal.aborted) { setAllRows([]); showToast("error", error.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, userDetails?.id, validCompany, refresh]);

  useEffect(() => () => mutationRef.current?.abort(), []);

  // One save / cancel at a time; errors show as a toast.
  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true;
    setBusy(true);
    const controller = new AbortController();
    mutationRef.current = controller;
    try {
      const value = await action(controller.signal);
      if (controller.signal.aborted) return { success: false };
      return { success: true, value };
    } catch (error) {
      if (!controller.signal.aborted) showToast("error", error.message);
      return { success: false };
    } finally {
      if (!controller.signal.aborted) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const tableContainerRef = useRef(null);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  // The sticky table header sits under the CustomFilter bar, whose height
  // changes once filter chips appear — measured, same as Users.
  useEffect(() => {
    if (!customFilterNode) return undefined;
    const update = () => setCustomFilterHeight(customFilterNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const update = () => setFooterHeight(footerNode.offsetHeight);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    scrollTop(tableContainerRef);
  }, [page]);

  const handleSavedViewChange = useCallback(() => setPage(0), []);

  const { visibleHeaders: savedViewHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "pay_input_loans_advances_saved_views",
    fullHeaders: loanAdvanceHeader,
    screenAccess: canEdit,
    onViewChange: handleSavedViewChange,
    fixedLeadingKeys: FIXED_COLUMNS,
  });
  // No approval step configured: the Status column is not shown.
  const visibleHeaders = useMemo(
    () => (approvalRequired ? savedViewHeaders : savedViewHeaders?.filter((head) => head.key !== "status")),
    [approvalRequired, savedViewHeaders],
  );
  const summaryCards = approvalRequired ? SUMMARY_CARDS : SUMMARY_CARDS.filter((card) => card.key !== "pending");

  // ---- Data pipeline: API rows -> CustomFilter ----

  const tabRows = useMemo(
    () =>
      [...allRows]
        .sort((a, b) => (a.issue_date < b.issue_date ? 1 : a.issue_date > b.issue_date ? -1 : 0))
        .map((row) => ({
          ...row,
          employee: `${row.employee_name} (${row.employee_code})`,
          department: row.department_name || row.department || "",
        })),
    [allRows],
  );

  // CustomFilter reports its result one render after tabRows changes; only
  // trust it when it was computed from the current tabRows (Users pattern).
  const [filteredRows, setFilteredRows] = useState(null);
  const filteredSourceRef = useRef(null);
  const visibleRows = filteredRows !== null && filteredSourceRef.current === tabRows ? filteredRows : tabRows;

  const handleFilteredDataChange = useCallback(
    (data) => {
      filteredSourceRef.current = tabRows;
      setFilteredRows(data);
      if (page * rowsPerPage >= data.length) setPage(0);
    },
    [page, rowsPerPage, tabRows],
  );

  // Cards use the listed rows (after the toolbar filters).
  const summary = useMemo(() => {
    const pending = visibleRows.filter((row) => row.status === "Pending");
    const active = visibleRows.filter(isActive);
    const loans = active.filter((row) => row.record_type === "Loan");
    const advances = active.filter((row) => row.record_type === "Advance");
    return {
      total: { value: count(visibleRows), detail: `${formatMoney(sum(visibleRows, "disbursed_amount"))} requested` },
      pending: { value: count(pending), detail: `${formatMoney(sum(pending, "disbursed_amount"))} requested` },
      loans: { value: count(loans), detail: `${formatMoney(sum(loans, "outstanding_amount"))} outstanding` },
      advances: { value: count(advances), detail: `${formatMoney(sum(advances, "outstanding_amount"))} outstanding` },
    };
  }, [visibleRows]);

  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const pageSelectedCount = pageRows.filter((row) => selectedIds.has(row.id)).length;
  const allPageSelected = pageRows.length > 0 && pageSelectedCount === pageRows.length;

  // Selection only ever covers rows that are still listed.
  useEffect(() => {
    setSelectedIds((current) => {
      const listed = new Set(visibleRows.map((row) => row.id));
      const next = new Set([...current].filter((id) => listed.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [visibleRows]);

  const togglePageSelection = () =>
    setSelectedIds((current) => {
      const next = new Set(current);
      pageRows.forEach((row) => (allPageSelected ? next.delete(row.id) : next.add(row.id)));
      return next;
    });

  const toggleRowSelection = (row) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });

  const guard = (action) => {
    if (!canEdit) {
      showToast("error", "Access denied: You are not authorized to change loans and advances");
      return;
    }
    action();
  };

  // ---- Save / cancel (advance/request/) ----

  // Saves, then reloads the list so totals and statuses come from the server.
  const handleLoanSave = async (form) => {
    const editing = loanPanel.mode === "edit";
    const result = await runAction((signal) => saveAdvanceRequest(domain, topbarCompanyId, form, editing ? loanPanel.rowId : null, signal));
    if (!result.success) return;
    setLoanPanel((current) => ({ ...current, open: false }));
    setRefresh((value) => value + 1);
    showToast("success", editing ? "Changes saved." : `${form.record_type} request created and sent for approval.`);
  };

  // Opening balance from before Cauver (not a new request).
  const handleMigratedSave = async (form) => {
    const result = await runAction((signal) => saveMigratedLoan(domain, topbarCompanyId, form, signal));
    if (!result.success) return;
    setMigratedPanel((current) => ({ ...current, open: false }));
    setRefresh((value) => value + 1);
    showToast("success", "Opening balance added.");
  };

  // Imported rows are Pending requests: reload to list them.
  const handleImported = (answer) => {
    const created = Number(answer?.success) || 0;
    if (!created) return;
    setRefresh((value) => value + 1);
    showToast("success", `${created} request${created === 1 ? "" : "s"} created and sent for approval.`);
  };

  // Only requests that haven't ended and have nothing repaid are cancelled;
  // the rest of the selection is skipped.
  const handleCancel = async () => {
    const ids = cancelIds.filter((id) => canCancelRequest(allRows.find((row) => row.id === id)));
    const skipped = cancelIds.length - ids.length;
    const reason = cancelReason.trim();
    const outcome = await runAction(async (signal) => {
      const results = await Promise.allSettled(ids.map((id) => cancelAdvanceRequest(domain, topbarCompanyId, id, reason, signal)));
      return {
        done: ids.filter((_, index) => results[index].status === "fulfilled"),
        errors: results.flatMap((result, index) => (result.status === "rejected" ? [`Record ${ids[index]}: ${result.reason.message}`] : [])),
      };
    });
    if (!outcome.success) return;
    const { done, errors } = outcome.value;
    setCancelIds(null);
    setCancelReason("");
    setSelectedIds(new Set());
    if (done.includes(viewRowId)) setViewRowId(null);
    if (done.length) {
      setRefresh((value) => value + 1);
      showToast("success", `${done.length} cancelled.`);
    }
    if (skipped) showToast("error", `${skipped} record(s) not cancelled: already ended or partly repaid.`);
    if (errors.length) showToast("error", errors.join(" "));
  };

  // Approve / Reject the ticked Pending requests one by one (the API has no
  // bulk call). Anything no longer Pending is skipped; the server's message
  // is shown for each failure (e.g. no approve permission, own request).
  const handleStatus = async () => {
    const { status, ids: picked } = statusAction;
    const rows = picked.map((id) => allRows.find((row) => row.id === id)).filter((row) => row?.status === "Pending");
    const skipped = picked.length - rows.length;
    const note = statusNote.trim();
    const approving = status === "Approved";
    const outcome = await runAction(async (signal) => {
      const results = await Promise.allSettled(
        rows.map((row) =>
          approving
            ? approveAdvanceRequest(domain, topbarCompanyId, row, note, signal)
            : rejectAdvanceRequest(domain, topbarCompanyId, row.id, note, signal),
        ),
      );
      return {
        done: rows.filter((_, index) => results[index].status === "fulfilled").map((row) => row.id),
        errors: results.flatMap((result, index) => (result.status === "rejected" ? [`${rows[index].employee_name || `Record ${rows[index].id}`}: ${result.reason.message}`] : [])),
      };
    });
    if (!outcome.success) return;
    const { done, errors } = outcome.value;
    setStatusAction(null);
    setStatusNote("");
    setSelectedIds(new Set());
    // Rejected requests leave the list (it hides Rejected), so close their view.
    if (!approving && done.includes(viewRowId)) setViewRowId(null);
    if (done.length) {
      setRefresh((value) => value + 1);
      showToast("success", `${done.length} ${approving ? "approved" : "rejected"}.`);
    }
    if (skipped) showToast("error", `${skipped} record(s) skipped: no longer pending.`);
    if (errors.length) showToast("error", errors.join(" "));
  };

  const openEdit = (row) => {
    if (!canEditRequest(row)) return;
    setLoanPanel({ open: true, mode: "edit", rowId: row.id, initial: recordToLoanForm(row) });
  };

  const viewRow = allRows.find((row) => row.id === viewRowId) ?? null;

  // View and Add/Edit share one expand state: Edit opened from an expanded
  // view opens expanded too. It resets once every panel is closed.
  const [panelExpanded, setPanelExpanded] = useState(false);
  const togglePanelExpanded = () => setPanelExpanded((value) => !value);
  const anyPanelOpen = Boolean(viewRow) || loanPanel.open || migratedPanel.open;
  useEffect(() => {
    if (!anyPanelOpen) setPanelExpanded(false);
  }, [anyPanelOpen]);

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  const renderCell = (row, key) => {
    switch (key) {
      case "issue_date":
        return formatDate(row.issue_date);
      case "employee":
        return <EmployeeCell name={row.employee_name} code={row.employee_code} />;
      case "record_type":
        return <ToneChip label={row.record_type} tone={TYPE_TONES[row.record_type]} />;
      case "source":
        return <ToneChip label={row.source} tone={SOURCE_TONES[row.source]} />;
      case "disbursed_amount":
      case "repaid_amount":
      case "outstanding_amount":
        return formatMoney(row[key]);
      case "progress_label":
        return <ProgressCell row={row} />;
      case "status":
        return <RequestStatusChip status={row.status} colorCode={statusColor(row)} />;
      default:
        return row[key] || "–";
    }
  };

  // The header strip itself is sticky, so its cells are not.
  const headCellSx = { ...sharedTableHeadCellSx, position: "static", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
  const colWidths = [52, ...(visibleHeaders ?? []).map((head) => COLUMN_WIDTHS[head.key] ?? 130)];
  // Checkbox, Employee and Department stay pinned while the rest scrolls.
  const pinned = pinnedColumns(colWidths, [
    0,
    ...(visibleHeaders ?? []).flatMap((head, index) => (PINNED_KEYS.has(head.key) ? [index + 1] : [])),
  ]);
  const pinSx = (index, head = false) =>
    index in pinned.lefts
      ? pinnedCellSx({ left: pinned.lefts[index], last: index === pinned.last, head })
      : head
        ? headScrollCellSx
        : {};
  const { headRef, bodyRef, proxyRef, proxyInnerRef } = useStickyHorizontalScroll([colWidths.join(","), pageRows.length, footerHeight]);

  const selected = [...selectedIds];
  const selectedAllPending =
    selected.length > 0 && selected.every((id) => allRows.find((row) => row.id === id)?.status === "Pending");
  const addItems = [
    ...(manualAllowed
      ? [
          {
            label: "Add Loan / Advance",
            icon: AddCircleOutline,
            onClick: () => setLoanPanel({ open: true, mode: "add", rowId: null, initial: emptyLoanForm() }),
          },
          // Opening balances from before Cauver (MigratedLoanPanel).
          {
            label: "Add Migrated Loan",
            icon: HistoryEduOutlined,
            onClick: () => setMigratedPanel({ open: true, initial: emptyMigratedForm() }),
          },
        ]
      : []),
    ...(importAllowed ? [{ label: "Import Loans & Advances", icon: FileUploadOutlined, onClick: () => setImportOpen(true) }] : []),
  ];
  // Search quick action "add-loan" (?action=add-loan) opens the Add form.
  useQuickActionFromUrl(
    { "add-loan": canEdit ? addItems.find((item) => item.label === "Add Loan / Advance")?.onClick : null },
    !loading && !configLoading,
  );

  // Turned off in Pay Input Configuration: the sidebar hides this screen, so
  // an old link or bookmark goes to the next Pay Inputs screen instead.
  // Nothing is drawn until the setting is known, so the list never flashes.
  if (configLoading) return <Loader info="Loading..." minHeight={`calc(100vh - ${APP_BAR_HEIGHT}px)`} />;
  if (!configEnabled) return <Redirect to="/admin/payroll/pay-inputs/salary-holds" />;

  return (
    <ThemeProvider theme={withDesignFont}>
      {/* Flex column so the table card (and its footer) reaches the bottom
          of the screen on short lists. */}
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2, display: "flex", flexDirection: "column" }}>
        <Mui.Box
          sx={{
            ...designShell.headerRowBase,
            alignItems: { xs: "flex-start", sm: "center" },
            px: 2,
            pt: 2,
            mb: 2,
          }}
        >
          <Mui.Box sx={{ minWidth: 0 }}>
            {/* Payroll goes to the Payroll home; Pay Inputs has no page of
                its own (sidebar only), so it stays plain text. */}
            <GlobalBreadcrumb
              trail={[
                { label: "Payroll", onClick: () => history.push("/admin/payroll/setup") },
                "Pay Inputs",
                "Loans & Advances",
              ]}
            />
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Loans & Advances
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Manage employee loans, salary advances, repayments and outstanding balances.
            </Mui.Typography>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.25} alignItems="center">
            {canEdit && addItems.length ? (
              <Mui.Button
                disabled={busy || loading || !validCompany}
                startIcon={<Add />}
                endIcon={<KeyboardArrowDown />}
                onClick={(event) => setAddMenuAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(addMenuAnchor)}
                sx={designShell.primaryButtonBase}
              >
                Add Loans & Advances
              </Mui.Button>
            ) : null}
            <OpenSettingsButton group="pay-input" item="loans-and-advances" />
          </Mui.Stack>
        </Mui.Box>

        <Mui.Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {addItems.map((item) => (
            <Mui.MenuItem
              key={item.label}
              disabled={item.disabled}
              sx={designShell.actionMenuItem}
              onClick={() => {
                setAddMenuAnchor(null);
                item.onClick();
              }}
            >
              <Mui.ListItemIcon>
                <item.icon fontSize="small" sx={{ color: designColors.tealDark }} />
              </Mui.ListItemIcon>
              {item.label}
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2, flex: "1 1 auto", display: "flex", flexDirection: "column" }}>
          {/* Source tabs from the mockup. The advance request API has no
              source for a request yet, so Imported / Manual Entries are
              shown disabled and everything is under All Records. */}
          <SourceTabs tabs={["all", "imported", "manual"]} value="all" rows={allRows} disabledTabs={["imported", "manual"]} onChange={() => {}} />

          {/* Summary for the listed rows (after any toolbar filters). */}
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: `repeat(${summaryCards.length}, minmax(0, 1fr))` },
              gap: 1.5,
              mb: 2,
            }}
          >
            {summaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={summary[card.key].value}
                detail={summary[card.key].detail}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          {/* Toolbar */}
          <Mui.Box
            ref={customFilterRef}
            sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}
          >
            <CustomFilter
              title="Loans & Advances"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={tabRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search..."
            />
          </Mui.Box>

          <BulkSelectionBar
            selectedCount={selected.length}
            onClear={() => setSelectedIds(new Set())}
            actions={[
              // Approve / Reject only while every ticked row is still
              // Pending: one already approved (or ended) hides them.
              ...(approvalRequired && selectedAllPending
                ? [
                    { label: "Approve", icon: CheckCircleOutline, onClick: () => guard(() => setStatusAction({ status: "Approved", ids: selected })) },
                    { label: "Reject", icon: HighlightOff, onClick: () => guard(() => setStatusAction({ status: "Rejected", ids: selected })), danger: true },
                  ]
                : []),
              { label: "Cancel", icon: CancelOutlined, onClick: () => guard(() => setCancelIds(selected)), danger: true },
            ]}
          />

          {/* Table: scrolls sideways inside the card with the header stuck
              under the toolbar and a sticky scrollbar above the footer. */}
          <Mui.Box
            sx={{
              ...designShell.sectionCard,
              mt: 1.5,
              borderRadius: 0,
              flex: "1 1 auto",
              display: "flex",
              flexDirection: "column",
              ...timelineScopeSx,
            }}
          >
            <StickyHeadStrip headRef={headRef} top={APP_BAR_OFFSET + customFilterHeight} colWidths={colWidths}>
              <Mui.TableHead>
                <Mui.TableRow>
                  <Mui.TableCell padding="checkbox" sx={{ ...headCellSx, ...pinSx(0, true) }}>
                    <Mui.Checkbox
                      size="small"
                      sx={bulkSelectionCheckboxSx}
                      checked={allPageSelected}
                      indeterminate={pageSelectedCount > 0 && !allPageSelected}
                      onChange={togglePageSelection}
                      inputProps={{ "aria-label": "Select all rows on this page" }}
                    />
                  </Mui.TableCell>
                  {visibleHeaders?.map((head, index) => (
                    <Mui.TableCell
                      key={head.key}
                      align={NUMERIC_KEYS.has(head.key) ? "right" : "left"}
                      sx={{ ...headCellSx, ...pinSx(index + 1, true) }}
                    >
                      <TruncatedTableValue value={head.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
            </StickyHeadStrip>
            <Mui.Box
              ref={(node) => {
                bodyRef.current = node;
                tableContainerRef.current = node;
              }}
              sx={{ ...scrollBodySx, flex: "1 1 auto", pb: footerHeight ? `${footerHeight}px` : 0 }}
            >
              <ApproveTable sx={fixedTableSx(colWidths)}>
                <ColGroup colWidths={colWidths} />
                {/* Zero-height header copy: phone cards are labelled from it. */}
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell padding="checkbox" sx={hiddenHeadCellSx} />
                    {visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} sx={hiddenHeadCellSx}>
                        <Mui.Box component="span">{head.name}</Mui.Box>
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={(visibleHeaders?.length ?? 0) + 1} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={tabRows} colSpan={(visibleHeaders?.length ?? 0) + 1} />
                  ) : (
                    pageRows.map((row) => (
                      <Mui.TableRow
                        key={row.id}
                        hover
                        onClick={() => setViewRowId(row.id)}
                        sx={{ cursor: "pointer", ...designShell.tableRowHover, "&:last-child td": { borderBottom: "none" } }}
                      >
                        <Mui.TableCell
                          padding="checkbox"
                          sx={{ ...sharedTableBodyCellSx, ...pinSx(0) }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Mui.Checkbox
                            size="small"
                            sx={bulkSelectionCheckboxSx}
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleRowSelection(row)}
                            inputProps={{ "aria-label": `Select ${row.employee_name}` }}
                          />
                        </Mui.TableCell>
                        {visibleHeaders.map((head, index) => (
                          <Mui.TableCell
                            key={head.key}
                            align={NUMERIC_KEYS.has(head.key) ? "right" : "left"}
                            sx={{ ...sharedTableBodyCellSx, ...pinSx(index + 1) }}
                          >
                            {truncatableCell(renderCell(row, head.key))}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.Box>
            <ProxyScrollbar proxyRef={proxyRef} proxyInnerRef={proxyInnerRef} bottom={Math.max(0, footerHeight - 1)} />

            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <LoanViewPanel
        open={Boolean(viewRow)}
        row={viewRow}
        canEdit={canEdit}
        showApproval={approvalRequired}
        // Only while open: GlobalSidePanel keeps showing an expanded panel
        // even after `open` turns false.
        expanded={Boolean(viewRow) && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setViewRowId(null)}
        busy={busy}
        onEdit={openEdit}
        onCancel={(row) => guard(() => setCancelIds([row.id]))}
        // Approve / Reject from the view: same dialog (comment / reason) as
        // the list's tick-box action, for this one request.
        onStatus={(row, status) => guard(() => setStatusAction({ status, ids: [row.id] }))}
      />

      <LoanFormPanel
        open={loanPanel.open}
        mode={loanPanel.mode}
        initial={loanPanel.initial}
        saving={busy}
        expanded={loanPanel.open && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setLoanPanel((current) => ({ ...current, open: false }))}
        onSave={handleLoanSave}
      />

      <MigratedLoanPanel
        open={migratedPanel.open}
        initial={migratedPanel.initial}
        saving={busy}
        expanded={migratedPanel.open && panelExpanded}
        onToggleExpand={togglePanelExpanded}
        onClose={() => setMigratedPanel((current) => ({ ...current, open: false }))}
        onSave={handleMigratedSave}
      />

      <LoanImport open={importOpen} onClose={() => setImportOpen(false)} onImported={handleImported} domain={domain} companyId={topbarCompanyId} />

      <GlobalConfirmDialog
        open={Boolean(cancelIds)}
        title="Cancel loans & advances"
        onCancel={() => {
          if (busy) return;
          setCancelIds(null);
          setCancelReason("");
        }}
        onConfirm={handleCancel}
        confirmLabel="Cancel Request"
        cancelLabel="Keep"
        loading={busy}
        loadingLabel="Cancelling..."
        confirmDisabled={!cancelReason.trim()}
        reasonValue={cancelReason}
        onReasonChange={setCancelReason}
        reasonPlaceholder="Enter a reason for cancelling"
        maxWidth="sm"
        danger
      >
        {/* mb: gap above the reason box (same as the Approvals dialogs). */}
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Cancel {cancelIds?.length ?? 0} request{cancelIds?.length === 1 ? "" : "s"}? Requests that have ended or are
          partly repaid are kept.
        </Mui.Typography>
      </GlobalConfirmDialog>

      {/* Approve: optional comment. Reject: reason required (API rule). */}
      <GlobalConfirmDialog
        open={Boolean(statusAction)}
        title={statusAction?.status === "Approved" ? "Approve loans & advances" : "Reject loans & advances"}
        onCancel={() => {
          if (busy) return;
          setStatusAction(null);
          setStatusNote("");
        }}
        onConfirm={handleStatus}
        confirmLabel={statusAction?.status === "Approved" ? "Approve" : "Reject"}
        cancelLabel="Cancel"
        loading={busy}
        loadingLabel={statusAction?.status === "Approved" ? "Approving..." : "Rejecting..."}
        confirmDisabled={statusAction?.status === "Rejected" && !statusNote.trim()}
        reasonValue={statusNote}
        onReasonChange={setStatusNote}
        reasonPlaceholder={statusAction?.status === "Approved" ? "Add a comment (optional)" : "Enter a reason for rejection"}
        maxWidth="sm"
        danger={statusAction?.status === "Rejected"}
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          {statusAction?.status === "Approved"
            ? `Approve ${statusAction?.ids.length ?? 0} request${statusAction?.ids.length === 1 ? "" : "s"} for the requested amount and repayment terms?`
            : `Reject ${statusAction?.ids.length ?? 0} request${statusAction?.ids.length === 1 ? "" : "s"}? The reason is shown to the employee.`}
        </Mui.Typography>
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
